import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Post {
  id: string;
  titleUz: string;
  titleRu: string;
  contentUz: string;
  contentRu: string;
  category: 'news' | 'announcement' | 'event';
  imageUrl: string;
  publishedAt: string;
  createdAt: string;
  isPublished: boolean;
}

export default function PostsPage() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const storedPosts = localStorage.getItem('medtour_posts_list');
    if (storedPosts) {
      const allPosts = JSON.parse(storedPosts);
      const publishedPosts = allPosts.filter((post: Post) => post.isPublished);
      setPosts(publishedPosts);
    }

    // Load view counts
    const storedViews = localStorage.getItem('post_view_counts');
    if (storedViews) {
      try { setViewCounts(JSON.parse(storedViews)); } catch { setViewCounts({}); }
    }
  }, []);

  // SEO meta + Schema.org
  useEffect(() => {
    const isUz = i18n.language === 'uz';
    const title = isUz ? 'Maqolalar - MediVoyage' : 'Публикации - MediVoyage';
    const description = isUz
      ? 'MediVoyage yangiliklari, e\'lonlar va tadbirlar. Koreya tibbiy turizmi haqida eng so\'nggi ma\'lumotlar.'
      : 'Новости, объявления и события MediVoyage. Последняя информация о медицинском туризме в Корее.';

    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', isUz
      ? 'koreya tibbiy yangiliklari, medvoyage maqolalar, tibbiy turizm yangiliklari'
      : 'новости медицинского туризма Корея, публикации MediVoyage, медицинские статьи');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.medivoyageuz.com';

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = `${siteUrl}/posts`;

    // OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description);
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', title);
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', description);

    // Schema.org Blog JSON-LD
    const schemaBlog = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      'name': isUz ? 'MediVoyage Maqolalar' : 'Публикации MediVoyage',
      'url': `${siteUrl}/posts`,
      'description': description,
      'inLanguage': isUz ? 'uz' : 'ru',
      'publisher': {
        '@type': 'Organization',
        'name': 'MediVoyage',
        'url': siteUrl,
      },
    };

    let scriptBlog = document.getElementById('schema-blog');
    if (!scriptBlog) {
      scriptBlog = document.createElement('script');
      scriptBlog.id = 'schema-blog';
      (scriptBlog as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(scriptBlog);
    }
    scriptBlog.textContent = JSON.stringify(schemaBlog);

    return () => {
      scriptBlog?.remove();
    };
  }, [i18n.language]);

  const handleOpenPost = (post: Post) => {
    setSelectedPost(post);
    // Increment view count
    const storedViews = localStorage.getItem('post_view_counts');
    const currentCounts: Record<string, number> = storedViews ? JSON.parse(storedViews) : {};
    const updated = { ...currentCounts, [post.id]: (currentCounts[post.id] || 0) + 1 };
    localStorage.setItem('post_view_counts', JSON.stringify(updated));
    setViewCounts(updated);
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, { uz: string; ru: string }> = {
      news: { uz: 'Yangiliklar', ru: 'Новости' },
      announcement: { uz: 'E\'lonlar', ru: 'Объявления' },
      event: { uz: 'Tadbirlar', ru: 'События' }
    };
    return i18n.language === 'uz' ? labels[category]?.uz : labels[category]?.ru;
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      news: 'bg-teal-100 text-teal-700',
      announcement: 'bg-orange-100 text-orange-700',
      event: 'bg-purple-100 text-purple-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'uz' ? 'uz-UZ' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {i18n.language === 'uz' ? 'Maqolalar' : 'Публикации'}
            </h1>
            <p className="text-lg text-gray-600">
              {i18n.language === 'uz' 
                ? 'Eng so\'nggi yangiliklar, e\'lonlar va tadbirlar' 
                : 'Последние новости, объявления и события'}
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {['all', 'news', 'announcement', 'event'].map((cat) => {
              const labels: Record<string, { uz: string; ru: string }> = {
                all: { uz: 'Barchasi', ru: 'Все' },
                news: { uz: 'Yangiliklar', ru: 'Новости' },
                announcement: { uz: 'E\'lonlar', ru: 'Объявления' },
                event: { uz: 'Tadbirlar', ru: 'События' },
              };
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    selectedCategory === cat
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {i18n.language === 'uz' ? labels[cat].uz : labels[cat].ru}
                </button>
              );
            })}
          </div>

          {/* Posts Grid */}
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-6xl text-gray-300"></i>
              </div>
              <p className="text-lg text-gray-500">
                {i18n.language === 'uz' 
                  ? 'Hozircha maqolalar yo\'q' 
                  : 'Пока нет публикаций'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <div
                  key={post.id}
                  onClick={() => handleOpenPost(post)}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  {/* Image */}
                  {post.imageUrl && (
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={post.imageUrl}
                        alt={i18n.language === 'uz' ? post.titleUz : post.titleRu}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6">
                    {/* Category Badge */}
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${getCategoryColor(post.category)}`}>
                      {getCategoryLabel(post.category)}
                    </span>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                      {i18n.language === 'uz' ? post.titleUz : post.titleRu}
                    </h3>

                    {/* Content Preview */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: (i18n.language === 'uz' ? post.contentUz : post.contentRu).replace(/<[^>]*>/g, '').slice(0, 150) }}
                    />

                    {/* Date & View Count */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatDate(post.publishedAt)}
                      </div>
                      <div className="flex items-center gap-1 text-gray-400">
                        <i className="ri-eye-line"></i>
                        <span>{(viewCounts[post.id] || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Post Detail Modal */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedPost.category)}`}>
                  {getCategoryLabel(selectedPost.category)}
                </span>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <i className="ri-eye-line"></i>
                  <span>
                    {(viewCounts[selectedPost.id] || 0).toLocaleString()}
                    {i18n.language === 'uz' ? ' ko\'rishlar' : ' просмотров'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedPost(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Image */}
              {selectedPost.imageUrl && (
                <div className="w-full h-64 overflow-hidden rounded-xl mb-6">
                  <img
                    src={selectedPost.imageUrl}
                    alt={i18n.language === 'uz' ? selectedPost.titleUz : selectedPost.titleRu}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              )}

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'uz' ? selectedPost.titleUz : selectedPost.titleRu}
              </h2>

              {/* Date & Views */}
              <div className="flex items-center gap-5 text-sm text-gray-500 mb-6">
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {formatDate(selectedPost.publishedAt)}
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-teal-500"></i>
                  <span className="font-medium text-teal-600">
                    {(viewCounts[selectedPost.id] || 0).toLocaleString()}
                  </span>
                  <span>{i18n.language === 'uz' ? 'marta ko\'rildi' : 'просмотров'}</span>
                </div>
              </div>

              {/* Content */}
              <div
                className="prose prose-lg max-w-none text-gray-700 rich-editor-content"
                style={{ lineHeight: '1.8' }}
                dangerouslySetInnerHTML={{ __html: i18n.language === 'uz' ? selectedPost.contentUz : selectedPost.contentRu }}
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}